// @ts-nocheck
import { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../api_actions';
import { orbitalSpinner } from '../LoadingOrbitalSpinner/LoadingOrbitalSpinner';

// Separate data transformation
const transformAccuracyData = (data) => {
    const transformedData = {};

    if (!data || data?.length === 0) {
        return [];
    }

    data.forEach(item => {
        if (!transformedData[item.type]) {
            transformedData[item.type] = {
                type: item.type,
                true_positive: 0,
                false_positive: 0,
                na: 0
            };
        }
        transformedData[item.type].true_positive += item.true_positive || 0;
        transformedData[item.type].false_positive += item.false_positive || 0;
        transformedData[item.type].na += item.na || 0;
    });
    return Object.values(transformedData);
};

// Separate API call
const fetchAccuracyData = async (deploymentId = null, startAndEndUnixDatesParams = '') => {
    try {
        const endpoint = deploymentId
            ? `read/accuracy_reports?deployment_id=${deploymentId}&${startAndEndUnixDatesParams()}`
            : `read/accuracy_reports?${startAndEndUnixDatesParams()}`;
        const res = await apiClient.post(endpoint);
        return transformAccuracyData(res);
    } catch (error) {
        console.error('Failed to load data:', error);
        return [];
    }
};

// Separate subtitle logic
const getSubtitle = (deploymentId, deployments) => {
    if (!deploymentId) return 'All Sites';
    const deployment = deployments.find(d => d.deployment_id === deploymentId);
    return deployment ? `${deployment.location?.name} - ${deployment.hardware_info?.tag}` : 'Selected Site';
};

// Base config (static)
const baseChartConfig = {
    series: [
        { type: 'bar', xKey: 'type', yKey: 'true_positive', yName: 'True Positive', stacked: true, fill: '#388E3C' },
        { type: 'bar', xKey: 'type', yKey: 'false_positive', yName: 'False Positive', stacked: true, fill: '#B71C1C' },
        { type: 'bar', xKey: 'type', yKey: 'na', yName: 'N/A', stacked: true, fill: '#546E7A' }
    ],
    axes: [
        {
            type: 'category',
            position: 'bottom',
            title: { text: 'Detection Type' },
        },
        {
            type: 'number',
            position: 'left',
            title: { text: 'Count' },
        },
    ],
    animation: { enabled: true, duration: 600 },
    title: { text: 'Model Accuracy Chart' },
};

// Factory function - simplified
const createModelAccuracyConfig = (deploymentId = null, deployments = [], hideUnknowns = false, startAndEndUnixDatesParams = '') => ({
    ...baseChartConfig,
    series: hideUnknowns
        ? baseChartConfig.series.filter(s => s.yKey !== 'na')
        : baseChartConfig.series,
    dataSource: {
        getData: () => fetchAccuracyData(deploymentId, startAndEndUnixDatesParams)
    },
    overlays: {
        loading: {
            renderer: orbitalSpinner,
        },
    },
    subtitle: { text: getSubtitle(deploymentId, deployments) },
});

export const SiteModelAccuracyChartLogic = (startAndEndUnixDatesParams = '', hideUnknowns = false) => {
    const [modelAccuracyConfig, setModelAccuracyConfig] = useState(null);
    const [deployments, setDeployments] = useState([]);
    const [selectedDeployment, setSelectedDeployment] = useState(null);

    const loadAllDeployments = async () => {
        try {
            const deploymentsRes = await apiClient.post(`read/deployments`);
            const deploymentsData = deploymentsRes?.data ?? [];
            setDeployments(deploymentsData);
            setModelAccuracyConfig(createModelAccuracyConfig(null, deploymentsData, hideUnknowns, startAndEndUnixDatesParams));
        } catch (error) {
            console.error('Failed to load deployments:', error);
        }
    };

    const getDeploymentOptions = () =>
        deployments.map(d => ({
            label: `${d.location?.name} - ${d.hardware_info?.tag}`,
            value: d.deployment_id,
            deployment: d,
        }));

    const filterOption = (input, option) => {
        const deployment = option?.deployment;
        if (!deployment) return false;
        const searchStr = `${deployment.location?.name ?? ''} - ${deployment.hardware_info?.tag ?? ''}`.toLowerCase();
        return searchStr.includes(input.toLowerCase());
    };

    const loadDeploymentData = (deploymentId) => {
        setSelectedDeployment(deploymentId);
        setModelAccuracyConfig(createModelAccuracyConfig(deploymentId, deployments, hideUnknowns));
    };

    const handleDeploymentChange = (deploymentId) => {
        if (deploymentId) {
            loadDeploymentData(deploymentId);
        } else {
            // If cleared, reload all sites data
            setSelectedDeployment(null);
            setModelAccuracyConfig(createModelAccuracyConfig(null, deployments, hideUnknowns));
        }
    };

    useEffect(() => {
        loadAllDeployments();
    }, []);

    return {
        modelAccuracyConfig,
        deployments,
        selectedDeployment,
        getDeploymentOptions,
        filterOption,
        handleDeploymentChange,
    };
};